import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import WithdrawalStatusMessage, { WITHDRAWAL_FAIL_REASONS } from '../components/WithdrawalStatusMessage';
import {
  listCashoutApplications,
  reviewCashoutApplication,
  listWithdrawalRequests,
  updateWithdrawalRequest,
  listPayoutMethods,
  reviewPayoutMethod,
  adminMakeUserEligible,
  CashoutApplication,
  CashoutApplicationStats,
  CashoutApplicationStatusFilter,
  WithdrawalRequest,
  WithdrawalRequestStats,
  WithdrawalRequestStatusFilter,
  PayoutMethod,
  PayoutMethodStats,
  PayoutMethodVerificationStatusFilter,
} from '../services/cashoutApi';

type ActiveTab = 'applications' | 'withdrawals' | 'bank_accounts';

interface PaginationState {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const defaultPagination: PaginationState = { page: 0, limit: 20, total: 0, totalPages: 1 };
const defaultAppStats: CashoutApplicationStats = { total: 0, pending: 0, approved: 0, rejected: 0 };
const defaultWdStats: WithdrawalRequestStats = { processing: 0, completed: 0, failed: 0 };
const defaultBankStats: PayoutMethodStats = { total: 0, pending: 0, approved: 0, rejected: 0 };

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getAppStatusBadgeClass = (status: string) => {
  if (status === 'PENDING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const getWdStatusBadgeClass = (status: string) => {
  if (status === 'processing') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'failed') return 'bg-rose-100 text-rose-700 border-rose-200';
  if (status === 'cancelled') return 'bg-slate-100 text-slate-600 border-slate-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const getBankVerificationBadgeClass = (status: string) => {
  if (status === 'PENDING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const Cashout: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('applications');

  // Applications state
  const [appStatusFilter, setAppStatusFilter] = useState<CashoutApplicationStatusFilter>('ALL');
  const [applications, setApplications] = useState<CashoutApplication[]>([]);
  const [appStats, setAppStats] = useState<CashoutApplicationStats>(defaultAppStats);
  const [appPagination, setAppPagination] = useState<PaginationState>(defaultPagination);
  const [appLoading, setAppLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState('');

  // Withdrawal requests state
  const [wdStatusFilter, setWdStatusFilter] = useState<WithdrawalRequestStatusFilter>('ALL');
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [wdStats, setWdStats] = useState<WithdrawalRequestStats>(defaultWdStats);
  const [wdPagination, setWdPagination] = useState<PaginationState>(defaultPagination);
  const [wdLoading, setWdLoading] = useState(false);
  const [wdActionLoading, setWdActionLoading] = useState('');
  const [failingRequestId, setFailingRequestId] = useState<string | null>(null);
  const [failReason, setFailReason] = useState('');

  // Bank accounts state
  const [bankStatusFilter, setBankStatusFilter] = useState<PayoutMethodVerificationStatusFilter>('ALL');
  const [bankMethods, setBankMethods] = useState<PayoutMethod[]>([]);
  const [bankStats, setBankStats] = useState<PayoutMethodStats>(defaultBankStats);
  const [bankPagination, setBankPagination] = useState<PaginationState>(defaultPagination);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankActionLoading, setBankActionLoading] = useState('');
  const [rejectingMethodId, setRejectingMethodId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  // Make eligible state
  const [eligibleUsername, setEligibleUsername] = useState('');
  const [eligibleLoading, setEligibleLoading] = useState(false);

  const loadApplications = useCallback(async (page: number = 0) => {
    setAppLoading(true);
    try {
      const response = await listCashoutApplications({ status: appStatusFilter, page, limit: defaultPagination.limit });
      setApplications(response.applications);
      setAppStats(response.stats);
      setAppPagination({
        page: response.page,
        limit: response.limit,
        total: response.total_count,
        totalPages: Math.max(1, response.total_pages),
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load cashout applications');
    } finally {
      setAppLoading(false);
    }
  }, [appStatusFilter]);

  const loadWithdrawals = useCallback(async (page: number = 0) => {
    setWdLoading(true);
    try {
      const response = await listWithdrawalRequests({ status: wdStatusFilter, page, limit: defaultPagination.limit });
      setWithdrawals(response.requests);
      setWdStats(response.stats);
      setWdPagination({
        page: response.page,
        limit: response.limit,
        total: response.total_count,
        totalPages: Math.max(1, response.total_pages),
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load withdrawal requests');
    } finally {
      setWdLoading(false);
    }
  }, [wdStatusFilter]);

  const loadBankMethods = useCallback(async (page: number = 0) => {
    setBankLoading(true);
    try {
      const response = await listPayoutMethods({ verification_status: bankStatusFilter, method_type: 'bank', page, limit: defaultPagination.limit });
      setBankMethods(response.methods);
      setBankStats(response.stats);
      setBankPagination({
        page: response.page,
        limit: response.limit,
        total: response.total_count,
        totalPages: Math.max(1, response.total_pages),
      });
    } catch (error: any) {
      toast.error(error.message || 'Failed to load bank accounts');
    } finally {
      setBankLoading(false);
    }
  }, [bankStatusFilter]);

  useEffect(() => {
    loadApplications(0);
  }, [loadApplications]);

  useEffect(() => {
    if (activeTab === 'withdrawals') loadWithdrawals(0);
  }, [activeTab, loadWithdrawals]);

  useEffect(() => {
    if (activeTab === 'bank_accounts') loadBankMethods(0);
  }, [activeTab, loadBankMethods]);

  const handleMakeEligible = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eligibleUsername.trim()) { toast.error('Enter a username'); return; }
    setEligibleLoading(true);
    try {
      const result = await adminMakeUserEligible(eligibleUsername.trim());
      toast.success(`${result.user_name} is now eligible for cashout`);
      setEligibleUsername('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to make user eligible');
    } finally {
      setEligibleLoading(false);
    }
  };

  const handleReview = async (e: React.MouseEvent, userId: string, status: 'APPROVED' | 'REJECTED') => {
    e.stopPropagation();
    if (status === 'REJECTED') {
      navigate(`/cashout/${encodeURIComponent(userId)}`);
      return;
    }
    const key = `${userId}:${status}`;
    setDecisionLoading(key);
    try {
      await reviewCashoutApplication(userId, status, undefined);
      toast.success('Application approved');
      await loadApplications(appPagination.page);
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve application');
    } finally {
      setDecisionLoading('');
    }
  };

  const handleApproveWithdrawal = async (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    setWdActionLoading(`${requestId}:complete`);
    try {
      await updateWithdrawalRequest(requestId, 'completed');
      toast.success('Withdrawal marked as completed');
      await loadWithdrawals(wdPagination.page);
    } catch (err: any) {
      toast.error(err.message || 'Failed to complete withdrawal');
    } finally {
      setWdActionLoading('');
    }
  };

  const handleFailWithdrawal = async (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    if (!failReason.trim()) {
      toast.error('A failure reason is required');
      return;
    }
    setWdActionLoading(`${requestId}:fail`);
    try {
      await updateWithdrawalRequest(requestId, 'failed', failReason.trim());
      toast.success('Withdrawal marked as failed');
      setFailingRequestId(null);
      setFailReason('');
      await loadWithdrawals(wdPagination.page);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update withdrawal');
    } finally {
      setWdActionLoading('');
    }
  };

  const handleApproveBankMethod = async (e: React.MouseEvent, methodId: string) => {
    e.stopPropagation();
    setBankActionLoading(`${methodId}:APPROVED`);
    try {
      await reviewPayoutMethod(methodId, 'APPROVED');
      toast.success('Bank account approved');
      await loadBankMethods(bankPagination.page);
    } catch (err: any) {
      toast.error(err.message || 'Failed to approve bank account');
    } finally {
      setBankActionLoading('');
    }
  };

  const handleRejectBankMethod = async (e: React.MouseEvent, methodId: string) => {
    e.stopPropagation();
    if (!rejectNote.trim()) {
      toast.error('A rejection reason is required');
      return;
    }
    setBankActionLoading(`${methodId}:REJECTED`);
    try {
      await reviewPayoutMethod(methodId, 'REJECTED', rejectNote.trim());
      toast.success('Bank account rejected');
      setRejectingMethodId(null);
      setRejectNote('');
      await loadBankMethods(bankPagination.page);
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject bank account');
    } finally {
      setBankActionLoading('');
    }
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900'>Cashout</h1>
            <p className='mt-2 text-gray-600'>Manage cashout applications and withdrawal requests.</p>
          </div>
          <div className='inline-flex items-center gap-2 self-start rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm'>
            <span className='h-2.5 w-2.5 rounded-full bg-emerald-500' />
            Connected to wss.tezda.tech
          </div>
        </div>

        {/* Make Eligible Panel */}
        <div className='mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
          <h2 className='mb-1 text-base font-semibold text-gray-900'>Make User Cashout Eligible</h2>
          <p className='mb-3 text-sm text-gray-500'>Override eligibility requirements for a specific user by username.</p>
          <form onSubmit={handleMakeEligible} className='flex gap-3'>
            <input
              type='text'
              value={eligibleUsername}
              onChange={(e) => setEligibleUsername(e.target.value)}
              placeholder='Enter username (e.g. john123)'
              className='flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
            />
            <button
              type='submit'
              disabled={eligibleLoading || !eligibleUsername.trim()}
              className='rounded-lg bg-gray-900 px-5 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {eligibleLoading ? 'Processing…' : 'Make Eligible'}
            </button>
          </form>
        </div>

        {/* Tabs */}
        <div className='mb-6 flex gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-sm w-fit'>
          <button
            onClick={() => setActiveTab('applications')}
            className={`rounded-lg px-5 py-2 text-sm font-semibold transition-colors ${activeTab === 'applications' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Applications
          </button>
          <button
            onClick={() => setActiveTab('withdrawals')}
            className={`rounded-lg px-5 py-2 text-sm font-semibold transition-colors ${activeTab === 'withdrawals' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Withdrawal Requests
          </button>
          <button
            onClick={() => setActiveTab('bank_accounts')}
            className={`rounded-lg px-5 py-2 text-sm font-semibold transition-colors ${activeTab === 'bank_accounts' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Bank Accounts
          </button>
        </div>

        {/* ── Applications Tab ─────────────────────────────────────────────── */}
        {activeTab === 'applications' && (
          <>
            <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4'>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Total</p>
                <p className='mt-1 text-3xl font-bold text-gray-900'>{appLoading ? '…' : appStats.total}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Pending</p>
                <p className='mt-1 text-3xl font-bold text-amber-600'>{appLoading ? '…' : appStats.pending}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Approved</p>
                <p className='mt-1 text-3xl font-bold text-emerald-600'>{appLoading ? '…' : appStats.approved}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Rejected</p>
                <p className='mt-1 text-3xl font-bold text-rose-600'>{appLoading ? '…' : appStats.rejected}</p>
              </div>
            </div>

            <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex flex-col gap-4 border-b border-gray-200 px-5 py-4 md:flex-row md:items-center md:justify-between'>
                <div>
                  <h2 className='text-lg font-semibold text-gray-900'>Applications</h2>
                  <p className='text-sm text-gray-500'>Click a row to review the application and user eligibility.</p>
                </div>
                <div className='flex flex-wrap items-center gap-3'>
                  <select
                    value={appStatusFilter}
                    onChange={(e) => setAppStatusFilter(e.target.value as CashoutApplicationStatusFilter)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  >
                    <option value='ALL'>All Statuses</option>
                    <option value='PENDING'>Pending</option>
                    <option value='APPROVED'>Approved</option>
                    <option value='REJECTED'>Rejected</option>
                  </select>
                  <button
                    onClick={() => loadApplications(appPagination.page)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Applied At</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reviewed At</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Note</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100 bg-white'>
                    {appLoading && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={6}>Loading applications…</td>
                      </tr>
                    )}
                    {!appLoading && applications.length === 0 && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={6}>No applications found for the selected filter.</td>
                      </tr>
                    )}
                    {!appLoading && applications.map((app) => {
                      const isApproveLoading = decisionLoading === `${app.user_id}:APPROVED`;
                      const isRejectLoading = decisionLoading === `${app.user_id}:REJECTED`;
                      const alreadyApproved = app.status === 'APPROVED';
                      const alreadyRejected = app.status === 'REJECTED';
                      return (
                        <tr
                          key={app.user_id}
                          onClick={() => navigate(`/cashout/${encodeURIComponent(app.user_id)}`)}
                          className='cursor-pointer transition-colors hover:bg-orange-50/50'
                        >
                          <td className='px-5 py-4 text-sm'>
                            {app.user_name
                              ? <span className='font-medium text-gray-900'>@{app.user_name}</span>
                              : <span className='font-mono text-xs text-gray-500'>{app.user_id}</span>
                            }
                            {app.user_name && (
                              <p className='font-mono text-xs text-gray-400'>{app.user_id}</p>
                            )}
                          </td>
                          <td className='px-5 py-4 text-sm'>
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getAppStatusBadgeClass(app.status)}`}>
                              {app.status}
                            </span>
                          </td>
                          <td className='px-5 py-4 text-sm text-gray-700'>{formatDate(app.applied_at)}</td>
                          <td className='px-5 py-4 text-sm text-gray-700'>{formatDate(app.reviewed_at)}</td>
                          <td className='px-5 py-4 text-sm text-gray-500 max-w-[200px] truncate' title={app.note || ''}>
                            {app.note || '—'}
                          </td>
                          <td className='px-5 py-4 text-sm' onClick={(e) => e.stopPropagation()}>
                            <div className='flex items-center gap-2'>
                              {!alreadyApproved && (
                                <button
                                  onClick={(e) => handleReview(e, app.user_id, 'APPROVED')}
                                  disabled={Boolean(decisionLoading)}
                                  className='rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
                                >
                                  {isApproveLoading ? 'Approving…' : alreadyRejected ? 'Re-Approve' : 'Approve'}
                                </button>
                              )}
                              {!alreadyRejected && (
                                <button
                                  onClick={(e) => handleReview(e, app.user_id, 'REJECTED')}
                                  disabled={Boolean(decisionLoading)}
                                  className='rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                >
                                  {isRejectLoading ? 'Rejecting…' : alreadyApproved ? 'Re-Reject' : 'Reject'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className='flex items-center justify-between border-t border-gray-200 px-5 py-4'>
                <p className='text-sm text-gray-600'>
                  Page {appPagination.page + 1} of {appPagination.totalPages} | Total {appPagination.total}
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => loadApplications(Math.max(0, appPagination.page - 1))}
                    disabled={appPagination.page <= 0 || appLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadApplications(Math.min(appPagination.totalPages - 1, appPagination.page + 1))}
                    disabled={appPagination.page >= appPagination.totalPages - 1 || appLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Withdrawal Requests Tab ──────────────────────────────────────── */}
        {activeTab === 'withdrawals' && (
          <>
            <div className='mb-6 grid grid-cols-3 gap-4'>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Processing</p>
                <p className='mt-1 text-3xl font-bold text-amber-600'>{wdLoading ? '…' : wdStats.processing}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Completed</p>
                <p className='mt-1 text-3xl font-bold text-emerald-600'>{wdLoading ? '…' : wdStats.completed}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Failed</p>
                <p className='mt-1 text-3xl font-bold text-rose-600'>{wdLoading ? '…' : wdStats.failed}</p>
              </div>
            </div>

            <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex flex-col gap-4 border-b border-gray-200 px-5 py-4 md:flex-row md:items-center md:justify-between'>
                <div>
                  <h2 className='text-lg font-semibold text-gray-900'>Withdrawal Requests</h2>
                  <p className='text-sm text-gray-500'>Approve or reject user withdrawal requests.</p>
                </div>
                <div className='flex flex-wrap items-center gap-3'>
                  <select
                    value={wdStatusFilter}
                    onChange={(e) => setWdStatusFilter(e.target.value as WithdrawalRequestStatusFilter)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  >
                    <option value='ALL'>All Statuses</option>
                    <option value='processing'>Processing</option>
                    <option value='completed'>Completed</option>
                    <option value='failed'>Failed</option>
                    <option value='cancelled'>Cancelled</option>
                  </select>
                  <button
                    onClick={() => loadWithdrawals(wdPagination.page)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Reference</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Method</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Tokens</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Net USD</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Created</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100 bg-white'>
                    {wdLoading && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={8}>Loading withdrawal requests…</td>
                      </tr>
                    )}
                    {!wdLoading && withdrawals.length === 0 && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={8}>No withdrawal requests found for the selected filter.</td>
                      </tr>
                    )}
                    {!wdLoading && withdrawals.map((wd) => {
                      const isProcessing = wd.status === 'processing';
                      const isCompleteLoading = wdActionLoading === `${wd.request_id}:complete`;
                      const isFailLoading = wdActionLoading === `${wd.request_id}:fail`;
                      const isExpanded = failingRequestId === wd.request_id;
                      return (
                        <React.Fragment key={wd.request_id}>
                          <tr
                            onClick={() => navigate(`/cashout/withdrawals/${encodeURIComponent(wd.request_id)}`)}
                            className='cursor-pointer transition-colors hover:bg-orange-50/50'
                          >
                            <td className='px-5 py-4 text-sm'>
                              {wd.user_name
                                ? <span className='font-medium text-gray-900'>@{wd.user_name}</span>
                                : <span className='font-mono text-xs text-gray-500'>{wd.user_id}</span>
                              }
                              {wd.user_name && (
                                <p className='font-mono text-xs text-gray-400'>{wd.user_id}</p>
                              )}
                            </td>
                            <td className='px-5 py-4 text-sm font-mono text-gray-700'>{wd.reference_id}</td>
                            <td className='px-5 py-4 text-sm'>
                              <p className='text-gray-700 capitalize'>{wd.method_type}</p>
                              {wd.method_label && (
                                <p className='text-xs text-gray-400'>{wd.method_label}</p>
                              )}
                            </td>
                            <td className='px-5 py-4 text-sm text-gray-700'>
                              {wd.tokens.toLocaleString()}
                              <p className='text-xs text-gray-400'>${wd.gross_usd.toFixed(2)} gross</p>
                            </td>
                            <td className='px-5 py-4 text-sm font-semibold text-gray-900'>${wd.net_usd.toFixed(2)}</td>
                            <td className='px-5 py-4 text-sm'>
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getWdStatusBadgeClass(wd.status)}`}>
                                {wd.status}
                              </span>
                            </td>
                            <td className='px-5 py-4 text-sm text-gray-500'>{formatDate(wd.created_at)}</td>
                            <td className='px-5 py-4 text-sm' onClick={(e) => e.stopPropagation()}>
                              {isProcessing ? (
                                <div className='flex items-center gap-2'>
                                  <button
                                    onClick={(e) => handleApproveWithdrawal(e, wd.request_id)}
                                    disabled={Boolean(wdActionLoading) || isExpanded}
                                    className='rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isCompleteLoading ? 'Marking…' : 'Complete'}
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setFailingRequestId(isExpanded ? null : wd.request_id); setFailReason(''); }}
                                    disabled={Boolean(wdActionLoading)}
                                    className='rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isFailLoading ? 'Failing…' : 'Fail'}
                                  </button>
                                </div>
                              ) : (
                                <span className='text-xs font-medium text-gray-400'>—</span>
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className='bg-rose-50'>
                              <td colSpan={8} className='px-5 py-3'>
                                <div className='flex items-center gap-3'>
                                  <select
                                    value={failReason}
                                    onChange={(e) => setFailReason(e.target.value)}
                                    className='flex-1 rounded-lg border border-rose-300 px-3 py-2 text-sm text-gray-700 focus:border-rose-500 focus:outline-none'
                                  >
                                    <option value=''>— Select failure reason —</option>
                                    {WITHDRAWAL_FAIL_REASONS.map((r) => (
                                      <option key={r} value={r}>{r}</option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={(e) => handleFailWithdrawal(e, wd.request_id)}
                                    disabled={Boolean(wdActionLoading)}
                                    className='rounded-md bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isFailLoading ? 'Submitting…' : 'Confirm Fail'}
                                  </button>
                                  <button
                                    onClick={() => { setFailingRequestId(null); setFailReason(''); }}
                                    className='rounded-md border border-gray-300 px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50'
                                  >
                                    Cancel
                                  </button>
                                </div>
                                {failReason.trim() && (
                                  <div className='mt-3'>
                                    <p className='mb-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide'>User will see →</p>
                                    <WithdrawalStatusMessage
                                      status='failed'
                                      failureReason={failReason.trim()}
                                      grossUsd={wd.gross_usd}
                                      referenceId={wd.reference_id}
                                      createdAt={wd.created_at}
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          {!isProcessing && !isExpanded && (wd.status === 'failed' || wd.status === 'cancelled') && (
                            <tr>
                              <td colSpan={8} className='px-5 pb-4 pt-0'>
                                <WithdrawalStatusMessage
                                  status={wd.status}
                                  failureReason={wd.failure_reason}
                                  processedAt={wd.processed_at}
                                  grossUsd={wd.gross_usd}
                                  referenceId={wd.reference_id}
                                  createdAt={wd.created_at}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className='flex items-center justify-between border-t border-gray-200 px-5 py-4'>
                <p className='text-sm text-gray-600'>
                  Page {wdPagination.page + 1} of {wdPagination.totalPages} | Total {wdPagination.total}
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => loadWithdrawals(Math.max(0, wdPagination.page - 1))}
                    disabled={wdPagination.page <= 0 || wdLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadWithdrawals(Math.min(wdPagination.totalPages - 1, wdPagination.page + 1))}
                    disabled={wdPagination.page >= wdPagination.totalPages - 1 || wdLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
        {/* ── Bank Accounts Tab ────────────────────────────────────────────── */}
        {activeTab === 'bank_accounts' && (
          <>
            <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4'>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Total</p>
                <p className='mt-1 text-3xl font-bold text-gray-900'>{bankLoading ? '…' : bankStats.total}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Pending</p>
                <p className='mt-1 text-3xl font-bold text-amber-600'>{bankLoading ? '…' : bankStats.pending}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Approved</p>
                <p className='mt-1 text-3xl font-bold text-emerald-600'>{bankLoading ? '…' : bankStats.approved}</p>
              </div>
              <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Rejected</p>
                <p className='mt-1 text-3xl font-bold text-rose-600'>{bankLoading ? '…' : bankStats.rejected}</p>
              </div>
            </div>

            <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex flex-col gap-4 border-b border-gray-200 px-5 py-4 md:flex-row md:items-center md:justify-between'>
                <div>
                  <h2 className='text-lg font-semibold text-gray-900'>Bank Accounts</h2>
                  <p className='text-sm text-gray-500'>Review and approve user bank account details for cashout.</p>
                </div>
                <div className='flex flex-wrap items-center gap-3'>
                  <select
                    value={bankStatusFilter}
                    onChange={(e) => setBankStatusFilter(e.target.value as PayoutMethodVerificationStatusFilter)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 focus:border-[#FF9C15] focus:outline-none'
                  >
                    <option value='ALL'>All Statuses</option>
                    <option value='PENDING'>Pending</option>
                    <option value='APPROVED'>Approved</option>
                    <option value='REJECTED'>Rejected</option>
                  </select>
                  <button
                    onClick={() => loadBankMethods(bankPagination.page)}
                    className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>User</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Bank</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Account Details</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Status</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Added</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-100 bg-white'>
                    {bankLoading && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={6}>Loading bank accounts…</td>
                      </tr>
                    )}
                    {!bankLoading && bankMethods.length === 0 && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={6}>No bank accounts found for the selected filter.</td>
                      </tr>
                    )}
                    {!bankLoading && bankMethods.map((method) => {
                      const isApproveLoading = bankActionLoading === `${method.method_id}:APPROVED`;
                      const isRejectLoading = bankActionLoading === `${method.method_id}:REJECTED`;
                      const isExpanded = rejectingMethodId === method.method_id;
                      const alreadyApproved = method.verification_status === 'APPROVED';
                      const alreadyRejected = method.verification_status === 'REJECTED';
                      return (
                        <React.Fragment key={method.method_id}>
                          <tr
                            onClick={() => navigate(`/cashout/bank-accounts/${encodeURIComponent(method.method_id)}`)}
                            className='cursor-pointer transition-colors hover:bg-orange-50/50'
                          >
                            <td className='px-5 py-4 text-sm'>
                              {method.user_name
                                ? <span className='font-medium text-gray-900'>@{method.user_name}</span>
                                : <span className='font-mono text-xs text-gray-500'>{method.user_id}</span>
                              }
                              {method.user_name && (
                                <p className='font-mono text-xs text-gray-400'>{method.user_id}</p>
                              )}
                            </td>
                            <td className='px-5 py-4 text-sm'>
                              <p className='font-medium text-gray-900'>{method.bank_name || '—'}</p>
                              {method.bank_country && <p className='text-xs text-gray-400'>{method.bank_city ? `${method.bank_city}, ` : ''}{method.bank_country}</p>}
                            </td>
                            <td className='px-5 py-4 text-sm'>
                              <p className='text-gray-700'>{method.account_holder_name || '—'}</p>
                              {method.account_number_last4 && <p className='text-xs text-gray-400'>****{method.account_number_last4}</p>}
                              {method.ifsc_code && <p className='text-xs text-gray-400'>IFSC: {method.ifsc_code}</p>}
                              {method.swift_bic && <p className='text-xs text-gray-400'>SWIFT: {method.swift_bic}</p>}
                              {method.iban_last4 && <p className='text-xs text-gray-400'>IBAN: ****{method.iban_last4}</p>}
                              {method.routing_number && <p className='text-xs text-gray-400'>Routing: {method.routing_number}</p>}
                              {method.sort_code && <p className='text-xs text-gray-400'>Sort: {method.sort_code}</p>}
                              {method.currency_code && <p className='text-xs text-gray-400'>{method.currency_code}</p>}
                            </td>
                            <td className='px-5 py-4 text-sm'>
                              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getBankVerificationBadgeClass(method.verification_status)}`}>
                                {method.verification_status}
                              </span>
                              {method.verification_note && (
                                <p className='mt-0.5 text-xs text-gray-400 max-w-[160px] truncate' title={method.verification_note}>{method.verification_note}</p>
                              )}
                            </td>
                            <td className='px-5 py-4 text-sm text-gray-500'>{formatDate(method.created_at)}</td>
                            <td className='px-5 py-4 text-sm' onClick={(e) => e.stopPropagation()}>
                              <div className='flex items-center gap-2'>
                                {!alreadyApproved && (
                                  <button
                                    onClick={(e) => handleApproveBankMethod(e, method.method_id)}
                                    disabled={Boolean(bankActionLoading) || isExpanded}
                                    className='rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isApproveLoading ? 'Approving…' : alreadyRejected ? 'Re-Approve' : 'Approve'}
                                  </button>
                                )}
                                {!alreadyRejected && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setRejectingMethodId(isExpanded ? null : method.method_id); setRejectNote(''); }}
                                    disabled={Boolean(bankActionLoading)}
                                    className='rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isRejectLoading ? 'Rejecting…' : alreadyApproved ? 'Re-Reject' : 'Reject'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className='bg-rose-50'>
                              <td colSpan={6} className='px-5 py-3'>
                                <div className='flex items-center gap-3'>
                                  <input
                                    type='text'
                                    value={rejectNote}
                                    onChange={(e) => setRejectNote(e.target.value)}
                                    placeholder='Enter rejection reason…'
                                    className='flex-1 rounded-lg border border-rose-300 px-3 py-2 text-sm text-gray-700 focus:border-rose-500 focus:outline-none'
                                  />
                                  <button
                                    onClick={(e) => handleRejectBankMethod(e, method.method_id)}
                                    disabled={Boolean(bankActionLoading)}
                                    className='rounded-md bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
                                  >
                                    {isRejectLoading ? 'Submitting…' : 'Confirm Reject'}
                                  </button>
                                  <button
                                    onClick={() => { setRejectingMethodId(null); setRejectNote(''); }}
                                    className='rounded-md border border-gray-300 px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50'
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className='flex items-center justify-between border-t border-gray-200 px-5 py-4'>
                <p className='text-sm text-gray-600'>
                  Page {bankPagination.page + 1} of {bankPagination.totalPages} | Total {bankPagination.total}
                </p>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => loadBankMethods(Math.max(0, bankPagination.page - 1))}
                    disabled={bankPagination.page <= 0 || bankLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadBankMethods(Math.min(bankPagination.totalPages - 1, bankPagination.page + 1))}
                    disabled={bankPagination.page >= bankPagination.totalPages - 1 || bankLoading}
                    className='rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Cashout;
